import { useEffect, useRef } from 'react'

type LiveWaveformProps = {
  analyser: AnalyserNode | null
  active: boolean
  className?: string
  color?: string
}

const BAR_WIDTH = 3
const BAR_GAP = 1
const MIN_BAR_HEIGHT = 4

export function LiveWaveform({ analyser, active, className, color = '#E40027' }: LiveWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const bufferRef = useRef<Float32Array<ArrayBuffer> | null>(null)
  const historyRef = useRef<number[]>([])

  useEffect(() => {
    if (!active || !analyser) {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      historyRef.current = []
      return
    }

    bufferRef.current = new Float32Array(analyser.fftSize)
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const draw = () => {
      const node = analyser
      const buf = bufferRef.current
      if (!node || !buf) return
      node.getFloatTimeDomainData(buf)

      let peak = 0
      for (let i = 0; i < buf.length; i++) {
        const v = Math.abs(buf[i])
        if (v > peak) peak = v
      }

      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      const w = Math.round(rect.width * dpr)
      const h = Math.round(rect.height * dpr)
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w
        canvas.height = h
      }

      const barStep = (BAR_WIDTH + BAR_GAP) * dpr
      const capacity = Math.max(1, Math.floor(w / barStep))
      const history = historyRef.current
      history.push(peak)
      if (history.length > capacity) history.splice(0, history.length - capacity)

      ctx.clearRect(0, 0, w, h)
      ctx.fillStyle = color

      const firstBarIdx = capacity - history.length
      for (let i = 0; i < history.length; i++) {
        const x = (firstBarIdx + i) * barStep
        const amp = Math.min(1, history[i])
        const barH = Math.max(MIN_BAR_HEIGHT * dpr, amp * h)
        const y = (h - barH) / 2
        ctx.fillRect(x, y, BAR_WIDTH * dpr, barH)
      }

      rafRef.current = requestAnimationFrame(draw)
    }
    rafRef.current = requestAnimationFrame(draw)

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [analyser, active, color])

  return <canvas ref={canvasRef} className={className ?? 'block h-10 w-full'} />
}
